import React, { createContext, useContext, useState, useEffect } from 'react'
import { useHistory } from "react-router-dom";
import { toast } from 'react-toastify';
import LeMoulinApi from 'app/universal/utils/LeMoulinApi'
import queryString from 'query-string';
import { initDate, initValue, initNumber, initBoolean, rangeDate, statusCode, initSelectedOrders } from 'app/orderTool/context/Utils'

const OrdersContext = createContext(null)
const OrdersProvider = (props) => {

  let history = useHistory();

  const [orders, setOrders]                 = useState([]);
  const [groupedOrders, setGroupedOrders]   = useState([]);
  const [countOrders, setCountOrders]       = useState(0);
  const [selectedOrders, setSelectedOrders] = useState(initSelectedOrders());
  const params                              = props.params;

  // Aucun paramètre d'URL → date du jour ; des paramètres mais sans date → toutes dates
  const hasAnyUrlParams = Object.keys(queryString.parse(window.location.search)).length > 0;

  const defaultFilters = {
    search: initValue('search'),
    startDate: initDate('startDate', hasAnyUrlParams),
    endDate: initDate('endDate', hasAnyUrlParams),
    type: initValue('type', 'quotation'),
    status: initValue('status', 'all_valid'),
    format: initValue('format', 'all'),
    startDatePaid: undefined,
    endDatePaid: undefined,
    chorus: initBoolean('chorus'),
    limit: 10,
    offset: initNumber('offset', 0),
    orderBy: initValue('orderBy', 'ASC'),
    tender_id: initValue('tender_id'),
    is_cancel: initBoolean('is_cancel'),
    to_upload_on_chorus: initBoolean('to_upload_on_chorus'),
    exclude_tender_order: initBoolean('exclude_tender_order'),
    only_service: initBoolean('only_service'),
    selectedOrders: selectedOrders,
    account_manager: initValue('account_manager')
  };

  const [dataParams, setDataParams] = useState((params) ? params : undefined);
  const [dataFilter, setDataFilter] = useState(defaultFilters);

  const [loading, setLoading] = useState(false);

  const fetchData = async () => {

    let filters;
    filters = {
      search: dataFilter.search,
      type: dataFilter.type,
      ...(dataFilter.format && dataFilter.format !== 'all' && { format: dataFilter.format }),
      limit: dataFilter.limit,
      offset: dataFilter.offset,
      order_by: {delivery_date : dataFilter.orderBy},
      selectedOrders: dataFilter.selectedOrders
    }
    filters.filters = {
      delivery_date: rangeDate(dataFilter.startDate,dataFilter.endDate),
      paid_date: rangeDate(dataFilter.startDatePaid,dataFilter.endDatePaid),
      total_price: dataFilter.price,
      total_price_ttc: dataFilter.price_ttc,
      status: statusCode(dataFilter.status),
      tender_id: dataFilter.tender_id,
      account_manager: dataFilter.account_manager
    }
    if (dataFilter.chorus === true) {
      filters.chorus = 1;
    }
    
    if (dataFilter.exclude_tender_order === true) {
      filters.exclude_tender_order = 1;
    }
    if (dataFilter.is_cancel === true) {
      filters.is_cancel = 1;
    }
    if (dataFilter.to_upload_on_chorus === true) {
      filters.to_upload_on_chorus = 1;
    }
    if (dataFilter.only_service === true) {
      filters.only_service = 1;
    }

    if (dataParams) {
      filters = {
        ...filters,
        ...dataParams
      }
    }

    const ordersResult = await LeMoulinApi.request.order.gets(filters);

    const normalizeReminderDate = (order) => {
      if (!order) {
        return order;
      }

      const reminderDate =
        order.last_missing_info_reminder_at ||
        order?.notification?.reminder_missing_field_mail_date ||
        null;

      if (!reminderDate) {
        return order;
      }

      return {
        ...order,
        notification: {
          ...(order.notification || {}),
          reminder_missing_field_mail_date: reminderDate,
        },
      };
    };

    const normalizedOrders = Array.isArray(ordersResult.orders)
      ? ordersResult.orders.map(normalizeReminderDate)
      : [];

    setOrders(normalizedOrders)
    setGroupedOrders(ordersResult.groupedOrders)
    setCountOrders(ordersResult.total)
    setLoading(false);

  }
  
  // A chaque fois qu'un filtre est mis à jour on déclence une requete
  useEffect(() => {
    setLoading(true);
    fetchData();

    let filtersInUrl = { ...dataFilter };

      if (filtersInUrl.selectedOrders) {
        try {
          filtersInUrl.selectedOrders = JSON.parse(filtersInUrl.selectedOrders);
        } catch (error) {
          filtersInUrl.selectedOrders = [];
        }
      }

    Object.keys(filtersInUrl).forEach((key) => {
      const value = filtersInUrl[key];
      if (value === undefined || value === null || value === '') {
          delete filtersInUrl[key];
        }
      });
      
    const combinedParams = {
      ...(dataParams || {}),
      ...filtersInUrl,
    };

    const searchParams = new URLSearchParams();

    Object.entries(combinedParams).forEach(([key, value]) => {
      if (value === undefined || value === null || value === '') {
        return;
      }

      if (typeof value === 'object') {
        searchParams.set(key, JSON.stringify(value));
      } else {
        searchParams.set(key, value);
      }
    });

    history.replace(`?${searchParams.toString()}`);
  }, [dataFilter, dataParams, history]);

  const load = (filters) => {
    fetchData();
  }
  
  /**
   * Appel la ressource qui notifie le client des champs manquants puis met à jour les commandes avec la commande mise à jour pour sa notification
   */
  const sendMissingFieldsCustomerNotification = async (orderId, orderType) => {
    try {
      const response = await LeMoulinApi.request.order.missingFieldsCustomerNotification(orderId, orderType);

      if (!response) {
        toast.error("Impossible d'envoyer la relance. Merci de réessayer.");
        return;
      }

      const looksLikeOrder = response?.order
        || (response && typeof response === "object" && "id" in response && (
          "info" in response || "company" in response || "delivery" in response || "missing_fields" in response
        ));

      const looksLikeNotification = !response?.order && response && typeof response === "object" && (
        "reminder_missing_field_mail_notified" in response
        || "missing_info_reminder_stages" in response
        || ("notification" in response && response.notification && typeof response.notification === "object")
      );

      const updatedOrder = response?.order ?? (looksLikeOrder ? response : null);

      const notificationPayload =
        response?.notification && typeof response.notification === "object"
          ? response.notification
          : (looksLikeNotification && !response?.notification ? response : null);

      let didUpdate = false;

      setOrders((prevOrders) =>
        prevOrders.map((order) => {
          if (order.id !== orderId) {
            return order;
          }

          if (updatedOrder) {
            didUpdate = true;
            return updatedOrder;
          }

          if (notificationPayload) {
            didUpdate = true;
            return {
              ...order,
              notification: {
                ...(order.notification || {}),
                ...notificationPayload,
              },
            };
          }

          return order;
        })
      );

      if (didUpdate) {
        toast.success("Relance envoyée au client.");
      } else {
        toast.error("La relance a été envoyée mais la commande n'a pas pu être mise à jour.");
      }
    } catch (error) {
      console.error("Impossible d'envoyer la relance des champs manquants", error);
      toast.error("L'envoi de la relance a échoué.");
    }
  }

  const data = {
    loading                              : loading,
    setLoading                           : (loading) => {setLoading(loading)},
    orders                               : orders,
    setOrders                            : (orders) => {setOrders(orders)},
    groupedOrders                        : groupedOrders,
    setGroupedOrders                     : (groupedOrders) => {setGroupedOrders(groupedOrders)},
    countOrders                          : countOrders,
    setCountOrders                       : (countOrders) => {setCountOrders(countOrders)},
    dataFilter                           : dataFilter,
    setDataFilter                        : (dataFilter) => {setDataFilter(dataFilter)},
    load                                 : (filters) => {load(filters)},
    sendMissingFieldsCustomerNotification: (orderId, orderType) => {sendMissingFieldsCustomerNotification(orderId, orderType)},
    selectedOrders                       : selectedOrders,
    setSelectedOrders                    : setSelectedOrders,
  }

  return (
    <OrdersContext.Provider value={data}>
      {props.children}
    </OrdersContext.Provider>
  )
}

export default OrdersProvider
export const useOrders = () => useContext(OrdersContext)
